import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaSpinner, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'sonner';
import { submitContactForm } from '@/api/contact';
import { content } from '@/content/sr';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = content.validation.required;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = content.validation.minLength.replace('{min}', '2');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = content.validation.required;
    } else if (!/^[+]?[\d\s-]{8,}$/.test(formData.phone.trim())) {
      newErrors.phone = content.validation.invalidPhone;
    }

    if (!formData.email.trim()) {
      newErrors.email = content.validation.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = content.validation.invalidEmail;
    }

    if (!formData.message.trim()) {
      newErrors.message = content.validation.required;
    } else if (formData.message.trim().length < 10) {
      newErrors.message = content.validation.minLength.replace('{min}', '10');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Molimo popunite sva polja ispravno.');
      return;
    }

    setStatus('loading');

    try {
      await submitContactForm(formData);
      setStatus('success');
      toast.success(content.contact.form.success);
      setFormData({ name: '', phone: '', email: '', message: '' });
      // Reset success status after 5 seconds
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error) {
      setStatus('error');
      toast.error(content.contact.form.error);
      // Reset error status after 3 seconds
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const inputClasses = (fieldName) =>
    `w-full bg-white border ${
      errors[fieldName] ? 'border-error' : 'border-neutral-200'
    } rounded-sm px-4 py-3 text-brand-primary placeholder:text-neutral-500 
    focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-accent
    transition-all duration-300`;

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="space-y-6"
      data-testid="contact-form"
    >
      {/* Name Field */}
      <div>
        <Label htmlFor="name" className="block text-sm font-medium text-brand-primary mb-2">
          {content.contact.form.name} *
        </Label>
        <Input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={inputClasses('name')}
          placeholder="Unesite ime i prezime"
          disabled={status === 'loading'}
          data-testid="contact-form-name"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-error" data-testid="error-name">{errors.name}</p>
        )}
      </div>

      {/* Phone Field */}
      <div>
        <Label htmlFor="phone" className="block text-sm font-medium text-brand-primary mb-2">
          {content.contact.form.phone} *
        </Label>
        <Input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={inputClasses('phone')}
          placeholder="+381 XX XXX XXXX"
          disabled={status === 'loading'}
          data-testid="contact-form-phone"
        />
        {errors.phone && (
          <p className="mt-1 text-sm text-error" data-testid="error-phone">{errors.phone}</p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <Label htmlFor="email" className="block text-sm font-medium text-brand-primary mb-2">
          {content.contact.form.email} *
        </Label>
        <Input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={inputClasses('email')}
          placeholder="vas@email.com"
          disabled={status === 'loading'}
          data-testid="contact-form-email"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-error" data-testid="error-email">{errors.email}</p>
        )}
      </div>

      {/* Message Field */}
      <div>
        <Label htmlFor="message" className="block text-sm font-medium text-brand-primary mb-2">
          {content.contact.form.message} *
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={5}
          className={inputClasses('message')}
          placeholder="Opišite vaš upit ili projekat..."
          disabled={status === 'loading'}
          data-testid="contact-form-message"
        />
        {errors.message && (
          <p className="mt-1 text-sm text-error" data-testid="error-message">{errors.message}</p>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === 'loading' || status === 'success'}
        className={`btn-accent w-full flex items-center justify-center gap-2 
                  ${status === 'loading' ? 'opacity-70 cursor-not-allowed' : ''}
                  ${status === 'success' ? 'bg-success hover:bg-success' : ''}
                  ${status === 'error' ? 'bg-error hover:bg-error' : ''}`}
        data-testid="contact-form-submit"
      >
        {status === 'loading' && (
          <>
            <FaSpinner className="animate-spin" />
            {content.contact.form.sending}
          </>
        )}
        {status === 'success' && (
          <>
            <FaCheck />
            Poslato!
          </>
        )}
        {status === 'error' && (
          <>
            <FaExclamationTriangle />
            Greška - Pokušajte ponovo
          </>
        )}
        {status === 'idle' && content.contact.form.submit}
      </button>
    </motion.form>
  );
};

export default ContactForm;
